import React, { useEffect, useMemo, useState } from "react";
import { crmSettingsApi } from "../../api/crmSettings";
import api from "../../../../shared/api/apiClient";
import styles from "./TelegramBotSettingsPage.module.css";
import { useTranslation } from "react-i18next";

const PASSWORD_MASK = "********";

const DEFAULT_LANGUAGE_LABELS = {
  ru: "🇷🇺 Русский",
  ro: "🇷🇴 Română",
  en: "🇬🇧 English",
};

const DEFAULT_TEXTS = {
  choose_language: {
    ru: "👇 Выберите язык для продолжения",
    ro: "👇 Alegeți limba pentru a continua",
    en: "👇 Please choose your language to continue",
  },
  age_birth_year_prompt: {
    ru: "👋 Здравствуйте!\nЧтобы продолжить, пожалуйста, укажите ваш год рождения.\n\nНапример: 2010",
    ro: "👋 Bună ziua!\nPentru a continua, vă rugăm să indicați anul nașterii.\n\nExemplu: 2010",
    en: "👋 Hello!\nTo continue, please enter your year of birth.\n\nExample: 2010",
    et: "👋 Tere!\nJätkamiseks palun sisestage oma sünniaasta.\n\nNäide: 2010",
    es: "👋 ¡Hola!\nPara continuar, indique su año de nacimiento.\n\nEjemplo: 2010",
  },
  welcome: {
    ru: "Здравствуйте 👋\n\n{greeting_company}\n\nЯ помогу быстро оставить заказ для менеджера.",
    ro: "Bună ziua 👋\n\n{greeting_company}\n\nVă ajut să lăsați rapid o comandă pentru manager.",
    en: "Hello 👋\n\n{greeting_company}\n\nI will help you quickly submit an order for the manager.",
  },
  phone_auth: {
    ru: "Пожалуйста, поделитесь телефоном, чтобы мы нашли вас в CRM или создали новый контакт.",
    ro: "Vă rugăm să partajați numărul de telefon, ca să vă găsim în CRM sau să creăm un contact nou.",
    en: "Please share your phone number so we can find you in CRM or create a new contact.",
  },
  share_phone: {
    ru: "📞 Поделиться телефоном",
    ro: "📞 Partajează telefonul",
    en: "📞 Share phone",
  },
  main_menu: {
    ru: "Теперь можно оформить заказ.",
    ro: "Acum puteți plasa comanda.",
    en: "Now you can submit your order.",
  },
  manual_order: {
    ru: "📝 Описать заказ",
    ro: "📝 Descrie comanda",
    en: "📝 Describe order",
  },
  marketing_button: {
    ru: "🎁 Акции",
    ro: "🎁 Promoții",
    en: "🎁 Promotions",
  },
  coupon_button: {
    ru: "🏷 Промокод",
    ro: "🏷 Cod promo",
    en: "🏷 Promo code",
  },
  phone_saved: {
    ru: "Спасибо! ✅\n\nМы получили ваш номер телефона.",
    ro: "Mulțumim! ✅\n\nAm primit numărul dvs. de telefon.",
    en: "Thank you! ✅\n\nWe received your phone number.",
  },
  ask_email_optional: {
    ru: "Если хотите получать уведомления о статусе заказа на электронную почту, укажите ваш e-mail. Если достаточно уведомлений по телефону, нажмите «Пропустить».",
    ro: "Doriți să primiți notificări despre starea comenzii prin e-mail? Introduceți adresa de e-mail sau apăsați „Omite”.",
    en: "Would you like to receive order status updates by email? Enter your email address or press \"Skip\".",
  },
  skip_email: {
    ru: "Пропустить",
    ro: "Omite",
    en: "Skip",
  },
  invalid_email: {
    ru: "❌ Email указан некорректно. Напишите email в формате name@example.com или нажмите «Пропустить».",
    ro: "❌ Emailul este incorect. Scrieți emailul în format name@example.com sau apăsați „Omite”.",
    en: "❌ The email is not valid. Enter an email like name@example.com or press “Skip”.",
  },
  email_saved: {
    ru: "Email сохранён ✅",
    ro: "Email salvat ✅",
    en: "Email saved ✅",
  },
  ask_order_description: {
    ru: "Опишите, что хотите заказать. Например: торт без сахара, 1 кг, на день рождения.",
    ro: "Descrieți ce doriți să comandați. De exemplu: tort fără zahăr, 1 kg, pentru zi de naștere.",
    en: "Describe what you want to order. Example: sugar-free cake, 1 kg, for a birthday.",
  },
  ask_order_address: {
    ru: "Укажите адрес доставки.",
    ro: "Indicați adresa de livrare.",
    en: "Please enter the delivery address.",
  },
  ask_order_date: {
    ru: "Укажите дату доставки. Например: {date}",
    ro: "Indicați data livrării. De exemplu: {date}",
    en: "Please enter the delivery date. Example: {date}",
  },
  invalid_order_date_format: {
    ru: "❌ Неверный формат даты. Укажите дату в формате ДД.ММ.ГГГГ. Например: {date}",
    ro: "❌ Format de dată incorect. Indicați data în formatul ZZ.LL.AAAA. De exemplu: {date}",
    en: "❌ Invalid date format. Please enter the date as DD.MM.YYYY. Example: {date}",
  },
  invalid_order_date_past: {
    ru: "❌ Нельзя выбрать дату из прошлого. Укажите сегодняшнюю или будущую дату. Например: {date}",
    ro: "❌ Nu puteți alege o dată din trecut. Indicați data de astăzi sau o dată viitoare. De exemplu: {date}",
    en: "❌ You cannot choose a past date. Please enter today or a future date. Example: {date}",
  },
  invalid_order_time_format: {
    ru: "❌ Неверный формат времени. Укажите время строго в формате ЧЧ:ММ. Например: 14:00 или 09:30",
    ro: "❌ Format de oră incorect. Indicați ora strict în formatul HH:MM. De exemplu: 14:00 sau 09:30",
    en: "❌ Invalid time format. Please enter the time strictly as HH:MM. Example: 14:00 or 09:30",
  },
  invalid_order_datetime_past: {
    ru: "❌ Дата и время некорректные. Укажите время в будущем, чтобы мы могли с вами связаться.",
    ro: "❌ Data și ora sunt incorecte. Indicați o oră în viitor, ca să vă putem contacta.",
    en: "❌ The date and time are incorrect. Please enter a future time so we can contact you.",
  },
  already_waiting: {
    ru: "⏳ Я уже жду ваш ответ. Напишите сообщение ниже.",
    ro: "⏳ Aștept deja răspunsul dvs. Scrieți mesajul mai jos.",
    en: "⏳ I am already waiting for your reply. Please write your message below.",
  },
  ask_order_time: {
    ru: "Укажите удобное время доставки. Например: 14:00",
    ro: "Indicați ora convenabilă pentru livrare. De exemplu: 14:00",
    en: "Please enter the preferred delivery time. Example: 14:00",
  },
  lead_created_with_email: {
    ru: "Заявка сохранена в CRM. Менеджер скоро свяжется с вами. Уведомления будут по телефону и по e-mail.",
    ro: "Cererea a fost salvată în CRM. Managerul vă va contacta în curând. Notificările vor fi trimise prin telefon și e-mail.",
    en: "Your request was saved in CRM. A manager will contact you soon. Notifications will be sent by phone and email.",
  },
  lead_created_without_email: {
    ru: "Заявка сохранена в CRM. Менеджер скоро свяжется с вами. Уведомления будут по телефону.",
    ro: "Cererea a fost salvată în CRM. Managerul vă va contacta în curând. Notificările vor fi trimise prin telefon.",
    en: "Your request was saved in CRM. A manager will contact you soon. Notifications will be sent by phone.",
  },
  unknown: {
    ru: "Нажмите кнопку «Описать заказ», чтобы оставить заявку.",
    ro: "Apăsați butonul „Descrie comanda” pentru a lăsa o cerere.",
    en: "Press “Describe order” to submit a request.",
  },
  phone_required_before_order: {
    ru: "Сначала поделитесь телефоном, чтобы мы могли прикрепить заказ к контакту.",
    ro: "Mai întâi partajați telefonul, ca să putem atașa comanda la contact.",
    en: "Please share your phone first so we can attach the order to your contact.",
  },
  ask_coupon_code: {
    ru: "Если у вас есть промокод, введите его одним сообщением или нажмите «Пропустить».",
    ro: "Dacă aveți un cod promo, introduceți-l într-un singur mesaj sau apăsați „Omite”.",
    en: "If you have a promo code, send it in one message or press “Skip”.",
  },
  coupon_code_placeholder: {
    ru: "Например: SALE-12345",
    ro: "De exemplu: SALE-12345",
    en: "Example: SALE-12345",
  },
  invalid_coupon_code: {
    ru: "❌ Промокод недоступен. Проверьте код и попробуйте ещё раз или нажмите «Пропустить».",
    ro: "❌ Codul promo nu este disponibil. Verificați codul și încercați din nou sau apăsați „Omite”.",
    en: "❌ Promo code is not available. Check the code and try again or press “Skip”.",
  },
};

const TEXT_FIELDS = [
  "welcome",
  "choose_language",
  "age_birth_year_prompt",
  "phone_auth",
  "share_phone",
  "main_menu",
  "manual_order",
  "marketing_button",
  "coupon_button",
  "phone_saved",
  "ask_email_optional",
  "skip_email",
  "invalid_email",
  "email_saved",
  "ask_order_description",
  "ask_order_address",
  "ask_order_date",
  "invalid_order_date_format",
  "invalid_order_date_past",
  "ask_order_time",
  "invalid_order_time_format",
  "invalid_order_datetime_past",
  "already_waiting",
  "lead_created_with_email",
  "lead_created_without_email",
  "unknown",
  "phone_required_before_order",
  "ask_coupon_code",
  "coupon_code_placeholder",
  "invalid_coupon_code",
];

const DEFAULT_LANGUAGES = [
  { code: "ru", label: DEFAULT_LANGUAGE_LABELS.ru, enabled: true, is_default: true },
  { code: "ro", label: DEFAULT_LANGUAGE_LABELS.ro, enabled: true, is_default: false },
  { code: "en", label: DEFAULT_LANGUAGE_LABELS.en, enabled: true, is_default: false },
];

const DEFAULT_DIALOG_STEPS = [
  {
    id: "order_description", key: "order_description", enabled: true, sort_order: 10,
    answer_type: "textarea", required: true, validation: "text", save_to: "lead.description",
    question: { ru: "Опишите, что хотите заказать", ro: "Descrieți ce doriți să comandați", en: "Describe what you want to order" },
    placeholder: { ru: "Например: торт без сахара, 1 кг", ro: "De exemplu: tort fără zahăr, 1 kg", en: "Example: sugar-free cake, 1 kg" },
  },
  {
    id: "delivery_address", key: "delivery_address", enabled: true, sort_order: 20,
    answer_type: "text", required: true, validation: "text", save_to: "task.description",
    question: { ru: "Укажите адрес доставки", ro: "Indicați adresa de livrare", en: "Enter the delivery address" },
    placeholder: { ru: "Город, улица, дом, квартира", ro: "Oraș, stradă, casă, apartament", en: "City, street, house, apartment" },
  },
  {
    id: "delivery_date", key: "delivery_date", enabled: true, sort_order: 30,
    answer_type: "date", required: true, validation: "future_or_today_date", save_to: "task.due_at",
    question: { ru: "Укажите дату доставки", ro: "Indicați data livrării", en: "Enter the delivery date" },
    placeholder: { ru: "ДД.ММ.ГГГГ", ro: "ZZ.LL.AAAA", en: "DD.MM.YYYY" },
  },
  {
    id: "delivery_time", key: "delivery_time", enabled: true, sort_order: 40,
    answer_type: "time", required: true, validation: "future_datetime", save_to: "task.due_at",
    question: { ru: "Укажите удобное время", ro: "Indicați ora convenabilă", en: "Enter the preferred time" },
    placeholder: { ru: "14:00", ro: "14:00", en: "14:00" },
  },
  {
    id: "promo_code", key: "promo_code", enabled: true, sort_order: 50,
    answer_type: "coupon", required: false, validation: "coupon_code", save_to: "coupon.code",
    question: { ru: "Введите промокод, если он у вас есть", ro: "Introduceți codul promo, dacă aveți unul", en: "Enter a promo code if you have one" },
    placeholder: { ru: "Например: SALE-12345 или нажмите «Пропустить»", ro: "De exemplu: SALE-12345 sau apăsați „Omite”", en: "Example: SALE-12345 or press “Skip”" },
  },
];

const DEFAULT_TELEGRAM = {
  enabled: false,
  bot_token: "",
  bot_username: "",
  public_base_url: "",
  manager_chat_id: "",
  conversation_flow: "product_orders",
  enable_language_selection: true,
  default_language: "ru",
  language_ru: true,
  language_ro: true,
  language_en: true,
  languages: DEFAULT_LANGUAGES,
  texts: DEFAULT_TEXTS,
  request_phone: true,
  create_contact: true,
  create_lead: true,
  create_task: true,
  use_crm_work_schedule: false,
  age_restriction_enabled: false,
  age_restriction_max_birth_year: "",
  notify_manager: true,
  enable_catalog: true,
  enable_manual_order: true,
  enable_marketing_menu: true,
  enable_coupon_menu: true,
  telegram_menu_layout: "compact",
  enable_manager_request: true,
  enable_reservation: true,
  attach_order_description_to_lead: true,
  show_product_price: true,
  show_stock: true,
  lead_source: "Telegram Bot",
  lead_status: "new",
  timezone: "Europe/Chisinau",
  internal_notifications_enabled: false,
  internal_chat_id: "",
  notify_new_leads: true,
  notify_manual_leads: true,
  notify_bot_leads: true,
  notify_api_leads: true,
  notify_import_leads: true,
  allow_assign_from_telegram: true,
  lead_message_template: "",
  internal_open_crm_base_url: "",
  welcome_message: "",
  order_form_fields: DEFAULT_DIALOG_STEPS,
};

const CONVERSATION_FLOWS = [
  { value: "product_orders", label: "telegram_flow_product_orders" },
  { value: "lead_capture", label: "telegram_flow_lead_capture" },
  { value: "catalog_assistant", label: "telegram_flow_catalog_assistant" },
  { value: "reservations", label: "telegram_flow_reservations" },
  { value: "support", label: "telegram_flow_support" },
];

const LEAD_STATUSES = [
  { value: "new", label: "telegram_lead_status_new" },
  { value: "contacted", label: "telegram_lead_status_contacted" },
  { value: "qualified", label: "telegram_lead_status_qualified" },
  { value: "proposal", label: "telegram_lead_status_proposal" },
  { value: "won", label: "telegram_lead_status_won" },
  { value: "lost", label: "telegram_lead_status_lost" },
];

const PAGE_TABS = [
  { id: "integration", label: "telegram_tab_integration" },
  { id: "languages", label: "telegram_languages" },
  { id: "dialog", label: "telegram_tab_dialog" },
  { id: "texts", label: "telegram_tab_texts" },
  { id: "automation", label: "telegram_tab_automation" },
  { id: "internal", label: "telegram_internal_sales_channel" },
];

const ANSWER_TYPES = [
  { value: "text", label: "telegram_answer_text" },
  { value: "textarea", label: "telegram_answer_long_text" },
  { value: "number", label: "telegram_answer_number" },
  { value: "date", label: "telegram_answer_date" },
  { value: "time", label: "telegram_answer_time" },
  { value: "datetime", label: "telegram_answer_datetime" },
  { value: "phone", label: "telegram_answer_phone" },
  { value: "email", label: "telegram_answer_email" },
  { value: "select", label: "telegram_answer_select" },
  { value: "photo", label: "telegram_answer_photo" },
  { value: "boolean", label: "telegram_answer_boolean" },
  { value: "coupon", label: "telegram_answer_coupon" },
];

const VALIDATION_RULES = [
  { value: "none", label: "telegram_validation_none" },
  { value: "text", label: "telegram_validation_text" },
  { value: "email", label: "telegram_answer_email" },
  { value: "phone", label: "telegram_answer_phone" },
  { value: "number", label: "telegram_answer_number" },
  { value: "future_or_today_date", label: "telegram_validation_future_date" },
  { value: "future_time", label: "telegram_validation_future_time" },
  { value: "future_datetime", label: "telegram_validation_future_datetime" },
  { value: "coupon_code", label: "telegram_validation_coupon" },
];

const SAVE_TARGETS = [
  { value: "lead.description", label: "telegram_save_lead_description" },
  { value: "lead.title", label: "telegram_save_lead_title" },
  { value: "lead.budget", label: "telegram_save_lead_budget" },
  { value: "lead.prepayment", label: "telegram_save_lead_prepayment" },
  { value: "task.description", label: "telegram_save_task_description" },
  { value: "task.due_at", label: "telegram_save_task_deadline" },
  { value: "contact.email", label: "telegram_save_contact_email" },
  { value: "contact.phone", label: "telegram_save_contact_phone" },
  { value: "coupon.code", label: "telegram_save_coupon_code" },
  { value: "custom_field", label: "telegram_save_custom_field" },
];

function boolValue(value) {
  return value === true;
}

function cleanLanguageCode(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 12);
}

function normalizeBaseUrl(value) {
  return String(value || "").trim().replace(/\/+$/, "");
}

function normalizeLanguages(data = {}) {
  if (Array.isArray(data.languages) && data.languages.length) {
    const seen = new Set();
    const result = data.languages
      .map((item) => {
        const code = cleanLanguageCode(item?.code);
        if (!code || seen.has(code)) return null;
        seen.add(code);
        return {
          code,
          label: String(item?.label || DEFAULT_LANGUAGE_LABELS[code] || code.toUpperCase()).trim(),
          enabled: item?.enabled !== false,
          is_default: item?.is_default === true,
        };
      })
      .filter(Boolean);

    if (result.length) {
      if (!result.some((x) => x.enabled)) result[0].enabled = true;
      if (!result.some((x) => x.enabled && x.is_default)) {
        const firstEnabled = result.find((x) => x.enabled);
        if (firstEnabled) firstEnabled.is_default = true;
      }
      return result;
    }
  }

  const legacy = DEFAULT_LANGUAGES.map((item) => ({
    ...item,
    enabled: data[`language_${item.code}`] !== false,
    is_default: (data.default_language || "ru") === item.code,
  }));

  if (!legacy.some((x) => x.enabled)) legacy[0].enabled = true;
  if (!legacy.some((x) => x.enabled && x.is_default)) {
    const firstEnabled = legacy.find((x) => x.enabled);
    if (firstEnabled) firstEnabled.is_default = true;
  }

  return legacy;
}

function mergeTexts(rawTexts, languages) {
  const texts = rawTexts && typeof rawTexts === "object" ? rawTexts : {};
  const result = {};
  const keys = Array.from(new Set([...Object.keys(DEFAULT_TEXTS), ...Object.keys(texts)]));
  const codes = languages.map((x) => x.code);

  keys.forEach((key) => {
    const incoming = texts[key] && typeof texts[key] === "object" ? texts[key] : {};
    result[key] = { ...incoming };
    codes.forEach((code) => {
      if (!String(result[key]?.[code] || "").trim()) {
        result[key][code] = DEFAULT_TEXTS[key]?.[code] || DEFAULT_TEXTS[key]?.ru || "";
      }
    });
  });

  return result;
}

function getDefaultLanguage(languages) {
  return (
    languages.find((x) => x.enabled && x.is_default)?.code ||
    languages.find((x) => x.enabled)?.code ||
    "ru"
  );
}

function makeDialogStepId() {
  return `field_${Date.now()}_${Math.random().toString(16).slice(2, 7)}`;
}

function looksLikePrepaymentStep(raw = {}, languages = []) {
  const values = [];
  const collect = (obj) => {
    if (obj && typeof obj === "object") Object.values(obj).forEach((v) => values.push(String(v || "")));
  };
  values.push(String(raw?.id || raw?.key || ""));
  collect(raw?.question);
  collect(raw?.label);
  collect(raw?.placeholder);
  const haystack = values.join(" ").toLowerCase();
  return /предоплат|аванс|prepayment|deposit|down payment|paid amount/.test(haystack);
}

function buildSelectOptionsFromPlaceholder(placeholder = {}, languages = [], fallbackOptions = []) {
  const linesByLanguage = {};
  let maxItems = 0;

  languages.forEach((language) => {
    const code = language.code;
    const lines = String(placeholder?.[code] || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    linesByLanguage[code] = lines;
    maxItems = Math.max(maxItems, lines.length);
  });

  // In the current dialog editor the multiline "Placeholder / example" field
  // is also the place where Select variants are entered: one option per line.
  if (maxItems > 0) {
    return Array.from({ length: maxItems }, (_, index) => {
      const label = {};
      languages.forEach((language) => {
        const code = language.code;
        if (linesByLanguage[code]?.[index]) label[code] = linesByLanguage[code][index];
      });

      const fallbackLabel = label.ru || label.en || Object.values(label).find(Boolean) || `Option ${index + 1}`;
      languages.forEach((language) => {
        if (!label[language.code]) label[language.code] = fallbackLabel;
      });

      return { value: fallbackLabel, label };
    });
  }

  return Array.isArray(fallbackOptions)
    ? fallbackOptions.map((option) => {
        if (!option || typeof option !== "object") return option;
        return { ...option, label: option.label && typeof option.label === "object" ? { ...option.label } : option.label };
      })
    : [];
}


function normalizeDialogStep(raw, index, languages) {
  const fallback = DEFAULT_DIALOG_STEPS[index] || DEFAULT_DIALOG_STEPS[0];
  const id = String(raw?.id || raw?.key || fallback?.id || makeDialogStepId()).trim().replace(/[^a-zA-Z0-9_-]/g, "_");
  const question = raw?.question && typeof raw.question === "object" ? raw.question : raw?.label && typeof raw.label === "object" ? raw.label : {};
  const placeholder = raw?.placeholder && typeof raw.placeholder === "object" ? raw.placeholder : {};
  const guessedPrepayment = looksLikePrepaymentStep(raw, languages);
  const incomingSaveTo = raw?.save_to || raw?.crm_target || fallback?.save_to || "lead.description";
  const result = {
    id, key: id, enabled: raw?.enabled !== false,
    sort_order: Number(raw?.sort_order ?? raw?.order ?? (index + 1) * 10),
    answer_type: raw?.answer_type || raw?.type || fallback?.answer_type || (guessedPrepayment ? "number" : "text"),
    required: raw?.required !== false,
    validation: raw?.validation || fallback?.validation || (guessedPrepayment ? "number" : "none"),
    save_to: guessedPrepayment && incomingSaveTo !== "lead.prepayment" ? "lead.prepayment" : incomingSaveTo,
    question: {}, placeholder: {},
    options: Array.isArray(raw?.options)
      ? raw.options.map((option) => (option && typeof option === "object" ? { ...option, label: option.label && typeof option.label === "object" ? { ...option.label } : option.label } : option))
      : (Array.isArray(fallback?.options) ? fallback.options : []),
    date_days_mode: raw?.date_days_mode || fallback?.date_days_mode || "all",
    date_allowed_weekdays: Array.isArray(raw?.date_allowed_weekdays)
      ? raw.date_allowed_weekdays.map(Number).filter((day) => day >= 0 && day <= 6)
      : (Array.isArray(fallback?.date_allowed_weekdays) ? fallback.date_allowed_weekdays : [0, 1, 2, 3, 4, 5, 6]),
    time_source: String(raw?.time_source || fallback?.time_source || "crm"),
    time_step_minutes: Number(raw?.time_step_minutes || fallback?.time_step_minutes || 15),
    time_range_enabled: raw?.time_range_enabled === true,
    time_from: String(raw?.time_from || fallback?.time_from || "09:00"),
    time_to: String(raw?.time_to || fallback?.time_to || "18:00"),
  };
  languages.forEach((lang) => {
    const code = lang.code;
    result.question[code] = question[code] || raw?.label?.[code] || fallback?.question?.[code] || fallback?.label?.[code] || "";
    result.placeholder[code] = placeholder[code] || fallback?.placeholder?.[code] || "";
  });

  if (String(result.answer_type || "").toLowerCase() === "select") {
    result.options = buildSelectOptionsFromPlaceholder(placeholder, languages, result.options);
  }

  return result;
}

function normalizeDialogSteps(rawSteps, languages) {
  const source = Array.isArray(rawSteps) && rawSteps.length ? [...rawSteps] : [...DEFAULT_DIALOG_STEPS];

  const hasPromoStep = source.some((step) => {
    const id = String(step?.id || step?.key || "").trim();
    return id === "promo_code";
  });

  if (!hasPromoStep) {
    const promoDefault = DEFAULT_DIALOG_STEPS.find((step) => step.id === "promo_code");
    if (promoDefault) {
      source.push({ ...promoDefault });
    }
  }

  return source
    .map((step, index) => normalizeDialogStep(step, index, languages))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((step, index) => ({ ...step, sort_order: (index + 1) * 10 }));
}

function migrateOldTelegramSettings(data) {
  const migrated = { ...(data || {}) };

  if (migrated.industry || migrated.industry_type) {
    migrated.conversation_flow = migrated.conversation_flow || "product_orders";
  }

  if (Object.prototype.hasOwnProperty.call(migrated, "warehouse_show_price")) {
    migrated.show_product_price = !!migrated.warehouse_show_price;
  }
  if (Object.prototype.hasOwnProperty.call(migrated, "warehouse_show_stock")) {
    migrated.show_stock = !!migrated.warehouse_show_stock;
  }
  if (Object.prototype.hasOwnProperty.call(migrated, "warehouse_allow_reservation")) {
    migrated.enable_reservation = !!migrated.warehouse_allow_reservation;
  }
  if (Object.prototype.hasOwnProperty.call(migrated, "quick_product_search")) {
    migrated.enable_catalog = !!migrated.quick_product_search;
  }
  if (Object.prototype.hasOwnProperty.call(migrated, "quick_manager")) {
    migrated.enable_manager_request = !!migrated.quick_manager;
  }
  if (Object.prototype.hasOwnProperty.call(migrated, "quick_reserve")) {
    migrated.enable_reservation = !!migrated.quick_reserve;
  }
  if (Object.prototype.hasOwnProperty.call(migrated, "quick_share_phone")) {
    migrated.request_phone = !!migrated.quick_share_phone;
  }

  delete migrated.industry;
  delete migrated.industry_type;
  delete migrated.allow_existing_clients;
  delete migrated.allow_new_clients;
  delete migrated.require_company_password;
  delete migrated.quick_product_search;
  delete migrated.quick_share_phone;
  delete migrated.quick_manager;
  delete migrated.quick_reserve;
  delete migrated.warehouse_search_by_name;
  delete migrated.warehouse_search_by_category;
  delete migrated.warehouse_search_by_sku;
  delete migrated.warehouse_show_price;
  delete migrated.warehouse_show_stock;
  delete migrated.warehouse_allow_reservation;
  delete migrated.language_et;

  const languages = normalizeLanguages(migrated);
  const defaultLanguage = getDefaultLanguage(languages);

  return {
    ...migrated,
    languages,
    default_language: defaultLanguage,
    language_ru: !!languages.find((x) => x.code === "ru" && x.enabled),
    language_ro: !!languages.find((x) => x.code === "ro" && x.enabled),
    language_en: !!languages.find((x) => x.code === "en" && x.enabled),
    texts: mergeTexts(migrated.texts, languages),
    order_form_fields: normalizeDialogSteps(
      migrated.order_form_fields || migrated.dialog_steps || migrated.dialog_schema,
      languages
    ),
    create_task: migrated.create_task !== false,
    use_crm_work_schedule: migrated.use_crm_work_schedule === true,
  };
}

function renderTelegramTemplatePreview(text, companyName = "Company Name", language = "en") {
  const safeCompanyName = String(companyName || "").trim();
  const greetingPrefix = language === "ru" ? "Вас приветствует" : language === "ro" ? "Vă întâmpină" : "Welcome from";
  const greetingCompany = safeCompanyName ? `${greetingPrefix} ${safeCompanyName}.` : "";

  return String(text || "")
    .replaceAll("{company_name}", safeCompanyName || "Company Name")
    .replaceAll("{greeting_company}", greetingCompany || `${greetingPrefix} Company Name.`)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function buildPayload(form) {
  const languages = normalizeLanguages(form).map((item) => ({
    ...item,
    is_default: item.enabled && item.code === form.default_language,
  }));

  const defaultLanguage = getDefaultLanguage(languages);

  return {
    ...form,
    public_base_url: normalizeBaseUrl(form.public_base_url),
    bot_username: String(form.bot_username || "").trim().replace(/^@+/, ""),
    languages,
    default_language: defaultLanguage,
    language_ru: !!languages.find((x) => x.code === "ru" && x.enabled),
    language_ro: !!languages.find((x) => x.code === "ro" && x.enabled),
    language_en: !!languages.find((x) => x.code === "en" && x.enabled),
    texts: mergeTexts(form.texts, languages),
    order_form_fields: normalizeDialogSteps(form.order_form_fields, languages),
  };
}

export default function TelegramBotSettingsPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState(DEFAULT_TELEGRAM);
  const [activePageTab, setActivePageTab] = useState("integration");
  const [activeTextLang, setActiveTextLang] = useState("ru");
  const [expandedDialogStepId, setExpandedDialogStepId] = useState("order_description");
  const [newLanguageCode, setNewLanguageCode] = useState("");
  const [newLanguageLabel, setNewLanguageLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [sessionStats, setSessionStats] = useState({
    total_sessions: 0,
    waiting_sessions: 0,
    order_dialog_sessions: 0,
  });

  const languages = useMemo(() => normalizeLanguages(form), [form]);
  const enabledLanguages = useMemo(() => languages.filter((x) => x.enabled), [languages]);
  const dialogSteps = useMemo(
    () => normalizeDialogSteps(form.order_form_fields, languages),
    [form.order_form_fields, languages]
  );

  const webhookUrl = useMemo(() => {
    const base = normalizeBaseUrl(form.public_base_url);
    if (!base) return t("telegram_set_public_base_url_first");
    return `${base}/webhooks/telegram/{tenant}/{secret}/receive`;
  }, [form.public_base_url, t]);

  const hasToken = boolValue(form.has_bot_token) || !!form.bot_token;

  const setValue = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const setChecked = (name, checked) => {
    setForm((prev) => ({ ...prev, [name]: !!checked }));
  };

  const updateLanguages = (updater) => {
    setForm((prev) => {
      const current = normalizeLanguages(prev);
      const next = updater(current).map((item) => ({ ...item }));
      if (!next.some((x) => x.enabled)) next[0].enabled = true;
      if (!next.some((x) => x.enabled && x.is_default)) {
        const firstEnabled = next.find((x) => x.enabled);
        if (firstEnabled) firstEnabled.is_default = true;
      }
      const defaultLanguage = getDefaultLanguage(next);
      return {
        ...prev,
        languages: next,
        default_language: defaultLanguage,
        texts: mergeTexts(prev.texts, next),
      };
    });
  };

  const updateLanguage = (code, patch) => {
    updateLanguages((items) =>
      items.map((item) => {
        if (item.code !== code) return item;
        const updated = { ...item, ...patch };
        if (patch.is_default) updated.enabled = true;
        return updated;
      }).map((item) => ({ ...item, is_default: patch.is_default && item.code !== code ? false : item.is_default }))
    );
  };

  const addLanguage = () => {
    const code = cleanLanguageCode(newLanguageCode);
    const label = String(newLanguageLabel || "").trim() || DEFAULT_LANGUAGE_LABELS[code] || code.toUpperCase();

    if (!code) {
      setErrorMessage(t("telegram_language_code_required"));
      return;
    }

    if (languages.some((x) => x.code === code)) {
      setErrorMessage(t("telegram_language_exists"));
      return;
    }

    updateLanguages((items) => [...items, { code, label, enabled: true, is_default: false }]);
    setActiveTextLang(code);
    setNewLanguageCode("");
    setNewLanguageLabel("");
    setErrorMessage("");
  };

  const removeLanguage = (code) => {
    if (languages.length <= 1) {
      setErrorMessage(t("telegram_language_minimum"));
      return;
    }
    updateLanguages((items) => items.filter((item) => item.code !== code));
    if (activeTextLang === code) {
      setActiveTextLang(getDefaultLanguage(languages.filter((x) => x.code !== code)));
    }
  };

  const setTextValue = (key, code, value) => {
    setForm((prev) => ({
      ...prev,
      texts: {
        ...(prev.texts || {}),
        [key]: {
          ...((prev.texts || {})[key] || {}),
          [code]: value,
        },
      },
    }));
  };

  const updateDialogSteps = (updater) => {
    setForm((prev) => {
      const currentLanguages = normalizeLanguages(prev);
      const current = normalizeDialogSteps(prev.order_form_fields, currentLanguages);
      const next = updater(current).map((step, index) => ({ ...step, sort_order: (index + 1) * 10 }));
      return { ...prev, order_form_fields: next };
    });
  };

  const updateDialogStep = (id, patch) => {
    updateDialogSteps((steps) => steps.map((step) => (step.id === id ? { ...step, ...patch } : step)));
  };

  const updateDialogStepLang = (id, field, code, value) => {
    updateDialogSteps((steps) =>
      steps.map((step) =>
        step.id === id ? { ...step, [field]: { ...(step[field] || {}), [code]: value } } : step
      )
    );
  };

  const moveDialogStep = (id, direction) => {
    updateDialogSteps((steps) => {
      const index = steps.findIndex((step) => step.id === id);
      if (index < 0) return steps;
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= steps.length) return steps;
      const next = [...steps];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const systemActions = useMemo(() => {
    const lang = activeTextLang || form.default_language || "ru";
    return [
      {
        id: "age_restriction",
        title: t("telegram_age_restriction_title"),
        enabled: form.age_restriction_enabled === true,
        field: "age_restriction_enabled",
        subtitle: "telegram_age_restriction_hint",
      },
      {
        id: "manual_order",
        title: form.texts?.manual_order?.[lang] || form.texts?.manual_order?.ru || DEFAULT_TEXTS.manual_order[lang] || DEFAULT_TEXTS.manual_order.en,
        enabled: form.enable_manual_order !== false,
        field: "enable_manual_order",
        subtitle: "telegram_action_manual_order_hint",
      },
      {
        id: "marketing",
        title: form.texts?.marketing_button?.[lang] || form.texts?.marketing_button?.ru || DEFAULT_TEXTS.marketing_button[lang] || DEFAULT_TEXTS.marketing_button.en,
        enabled: form.enable_marketing_menu !== false,
        field: "enable_marketing_menu",
        subtitle: "telegram_action_marketing_hint",
      },
      {
        id: "coupon",
        title: form.texts?.coupon_button?.[lang] || form.texts?.coupon_button?.ru || DEFAULT_TEXTS.coupon_button[lang] || DEFAULT_TEXTS.coupon_button.en,
        enabled: form.enable_coupon_menu !== false,
        field: "enable_coupon_menu",
        subtitle: "telegram_action_coupon_hint",
      },
    ];
  }, [activeTextLang, form.default_language, form.texts, form.age_restriction_enabled, form.enable_manual_order, form.enable_marketing_menu, form.enable_coupon_menu, t]);


  const addDialogStep = () => {
    const id = makeDialogStepId();
    const question = {};
    const placeholder = {};
    languages.forEach((language) => { question[language.code] = ""; placeholder[language.code] = ""; });
    updateDialogSteps((steps) => [
      ...steps,
      { id, key: id, enabled: true, sort_order: (steps.length + 1) * 10, answer_type: "text", required: true, validation: "text", save_to: "lead.description", question, placeholder },
    ]);
    setExpandedDialogStepId(id);
    setActivePageTab("dialog");
  };

  const removeDialogStep = (id) => {
    if (!window.confirm(t("telegram_remove_question_confirm"))) return;
    updateDialogSteps((steps) => steps.filter((step) => step.id !== id));
    if (expandedDialogStepId === id) setExpandedDialogStepId("");
  };

  const loadSessionStats = async () => {
    try {
      const stats = await crmSettingsApi.getTelegramSessionStats();
      setSessionStats({
        total_sessions: Number(stats?.total_sessions || 0),
        waiting_sessions: Number(stats?.waiting_sessions || 0),
        order_dialog_sessions: Number(stats?.order_dialog_sessions || 0),
      });
    } catch (e) {
      setSessionStats({
        total_sessions: 0,
        waiting_sessions: 0,
        order_dialog_sessions: 0,
      });
    }
  };

  const load = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const data = await crmSettingsApi.getTelegramSettings();
      const migrated = migrateOldTelegramSettings(data);
      const nextForm = {
        ...DEFAULT_TELEGRAM,
        ...migrated,
        bot_token: migrated?.has_bot_token ? PASSWORD_MASK : migrated?.bot_token || "",
      };
      setForm(nextForm);
      setActiveTextLang(getDefaultLanguage(nextForm.languages));
      await loadSessionStats();
    } catch (e) {
      setErrorMessage(e?.response?.data?.detail || e?.message || "Failed to load Telegram settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const payload = buildPayload(form);
      const res = await crmSettingsApi.updateTelegramSettings(payload);
      const telegram = migrateOldTelegramSettings(res?.telegram || payload);

      setForm({
        ...DEFAULT_TELEGRAM,
        ...telegram,
        bot_token: telegram?.has_bot_token ? PASSWORD_MASK : telegram?.bot_token || "",
      });
      setActiveTextLang(getDefaultLanguage(telegram.languages));
      setStatusMessage(t("telegram_saved_success"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErrorMessage(
        typeof detail === "string"
          ? detail
          : detail?.message || e?.message || "Failed to save Telegram settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const reset = () => load();

  const setWebhook = async () => {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const res = await crmSettingsApi.setTelegramWebhook();
      setStatusMessage(res?.message || "Webhook set successfully.");
      await load();
    } catch (e) {
      setErrorMessage(e?.response?.data?.detail || e?.message || "Failed to set webhook");
    } finally {
      setSaving(false);
    }
  };

  const deleteWebhook = async () => {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const res = await crmSettingsApi.deleteTelegramWebhook();
      setStatusMessage(res?.message || "Webhook disabled.");
      await load();
    } catch (e) {
      setErrorMessage(e?.response?.data?.detail || e?.message || "Failed to disable webhook");
    } finally {
      setSaving(false);
    }
  };

  const testBot = async () => {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const res = await crmSettingsApi.testTelegramBot({});
      setStatusMessage(res?.message || "Test message sent.");
    } catch (e) {
      setErrorMessage(
        e?.response?.data?.detail || e?.message || "Failed to send test message. Manager Chat ID is required."
      );
    } finally {
      setSaving(false);
    }
  };

  const testInternalChannel = async () => {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      await save();
      const res = await api.post("/auth/telegram/internal/test-channel");
      setStatusMessage(res?.data?.message || "Internal channel test sent.");
    } catch (e) {
      setErrorMessage(e?.response?.data?.detail || e?.message || "Failed to send internal channel test.");
    } finally {
      setSaving(false);
    }
  };

  const testInternalLeadNotification = async () => {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      await save();
      const res = await api.post("/auth/telegram/internal/test-lead-notification");
      setStatusMessage(res?.data?.message || "Test lead notification sent.");
    } catch (e) {
      setErrorMessage(e?.response?.data?.detail || e?.message || "Failed to send test lead notification.");
    } finally {
      setSaving(false);
    }
  };

  const restartFlows = async () => {
    if (
      !window.confirm(
        t("telegram_restart_flows_confirm")
      )
    ) {
      return;
    }

    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const res = await crmSettingsApi.restartTelegramFlows();
      setStatusMessage(
        t("telegram_maintenance_result", {
          message:
            res?.message || t("telegram_flows_restarted"),
          count: res?.affected_sessions ?? 0,
        }),
      );
      await loadSessionStats();
    } catch (e) {
      setErrorMessage(
        e?.response?.data?.detail ||
          e?.message ||
          t("telegram_restart_flows_error"),
      );
    } finally {
      setSaving(false);
    }
  };

  const resetAllSessions = async () => {
    if (
      !window.confirm(
        t("telegram_reset_sessions_confirm")
      )
    ) {
      return;
    }

    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const res = await crmSettingsApi.resetAllTelegramSessions();
      setStatusMessage(
        t("telegram_maintenance_result", {
          message:
            res?.message || t("telegram_sessions_reset"),
          count: res?.affected_sessions ?? 0,
        }),
      );
      await loadSessionStats();
    } catch (e) {
      setErrorMessage(
        e?.response?.data?.detail ||
          e?.message ||
          t("telegram_reset_sessions_error"),
      );
    } finally {
      setSaving(false);
    }
  };

  const resetAndSendWelcome = async () => {
    if (
      !window.confirm(
        t("telegram_reset_welcome_confirm")
      )
    ) {
      return;
    }

    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const res = await crmSettingsApi.resetTelegramSessionsAndSendWelcome();
      setStatusMessage(
        t("telegram_maintenance_result", {
          message:
            res?.message || t("telegram_sessions_reset_welcome_sent"),
          count: res?.affected_sessions ?? 0,
        }),
      );
      await loadSessionStats();
    } catch (e) {
      setErrorMessage(
        e?.response?.data?.detail ||
        e?.message ||
        t("telegram_reset_welcome_error"),
      );
    } finally {
      setSaving(false);
    }
  };

  const disabled = loading || saving;

  if (loading) {
    return <div className={styles.page}>{t("telegram_loading_settings")}</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <div className={styles.eyebrow}>{t("telegram_tenant_integration")}</div>
          <h1 className={styles.h1}>{t("telegram_bot")}</h1>
          <p className={styles.subtitle}>
            {t("telegram_page_description")}
          </p>
        </div>

        <div className={styles.actions}>
          <button className={styles.secondaryAction} onClick={reset} disabled={disabled}>
            {t("reset")}
          </button>
          <button className={styles.primaryAction} onClick={save} disabled={disabled}>
            {saving ? t("saving") : t("save_changes")}
          </button>
        </div>
      </div>

      {!!statusMessage && <div className={styles.success}>{statusMessage}</div>}
      {!!errorMessage && <div className={styles.error}>{errorMessage}</div>}

      <div className={styles.pageTabs}>
        {PAGE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activePageTab === tab.id ? styles.pageTabActive : styles.pageTab}
            onClick={() => setActivePageTab(tab.id)}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>

      <div className={styles.layout}>
        <div className={styles.mainCol}>
          {activePageTab === "integration" && (
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <div>
                  <div className={styles.cardTitle}>{t("telegram_integration")}</div>
                  <div className={styles.cardSub}>{t("telegram_integration_description")}</div>
                </div>

                <label className={styles.toggle}>
                  <input type="checkbox" checked={!!form.enabled} onChange={(e) => setChecked("enabled", e.target.checked)} />
                  <span>{form.enabled ? "Enabled" : "Disabled"}</span>
                </label>
              </div>

              <div className={styles.formGrid}>
                <label className={styles.formLabel}>{t("telegram_bot_token")}</label>
                <input className={styles.textInput} type="password" value={form.bot_token || ""} placeholder="123456789:ABC..." onChange={(e) => setValue("bot_token", e.target.value)} />

                <label className={styles.formLabel}>{t("telegram_bot_username")}</label>
                <input className={styles.textInput} value={form.bot_username || ""} placeholder="velsoft_crm_test_bot" onChange={(e) => setValue("bot_username", e.target.value)} />

                <label className={styles.formLabel}>{t("telegram_public_base_url")}</label>
                <input className={styles.textInput} value={form.public_base_url || ""} placeholder="https://demo.velsoftcrm.com" onChange={(e) => setValue("public_base_url", e.target.value)} />

                <label className={styles.formLabel}>{t("telegram_webhook_url")}</label>
                <input className={styles.textInputMuted} value={webhookUrl} readOnly />

                <label className={styles.formLabel}>{t("telegram_manager_chat_id")}</label>
                <input className={styles.textInput} value={form.manager_chat_id || ""} placeholder={t("telegram_manager_chat_placeholder")} onChange={(e) => setValue("manager_chat_id", e.target.value)} />
              </div>
            </section>
          )}

          {activePageTab === "languages" && (
            <section className={styles.card}>
              <div className={styles.cardTitle}>{t("telegram_languages")}</div>
              <div className={styles.cardSub}>
                {t("telegram_languages_description")}
              </div>

              <div className={styles.switchGrid}>
                <label className={styles.switchCard}>
                  <span>{t("telegram_language_selection")}</span>
                  <input type="checkbox" checked={!!form.enable_language_selection} onChange={(e) => setChecked("enable_language_selection", e.target.checked)} />
                </label>

                {languages.map((language) => (
                  <label className={styles.switchCard} key={language.code}>
                    <span>{language.label}</span>
                    <input type="checkbox" checked={!!language.enabled} onChange={(e) => updateLanguage(language.code, { enabled: e.target.checked })} />
                  </label>
                ))}
              </div>

              <div className={styles.row2}>
                <div className={styles.field}>
                  <label>{t("telegram_new_language_code")}</label>
                  <input className={styles.textInput} value={newLanguageCode} placeholder="bg" onChange={(e) => setNewLanguageCode(e.target.value)} />
                </div>
                <div className={styles.field}>
                  <label>{t("telegram_new_language_label")}</label>
                  <input className={styles.textInput} value={newLanguageLabel} placeholder="🇧🇬 Български" onChange={(e) => setNewLanguageLabel(e.target.value)} />
                </div>
              </div>

              <button className={styles.fullSecondary} type="button" onClick={addLanguage} disabled={disabled}>
                {t("telegram_add_language")}
              </button>

              <div className={styles.statusRows}>
                {languages.map((language) => (
                  <div key={language.code}>
                    <span>{language.code.toUpperCase()}</span>
                    <strong>
                      <button className={styles.secondaryAction} type="button" onClick={() => updateLanguage(language.code, { is_default: true })} disabled={disabled || !language.enabled}>
                        {language.is_default ? t("telegram_default") : t("telegram_make_default")}
                      </button>{" "}
                      <button className={styles.secondaryAction} type="button" onClick={() => removeLanguage(language.code)} disabled={disabled || languages.length <= 1}>
                        {t("remove")}
                      </button>
                    </strong>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activePageTab === "texts" && (
            <section className={styles.card}>
              <div className={styles.cardTitle}>{t("telegram_bot_messages")}</div>
              <div className={styles.cardSub}>
                {t("telegram_bot_messages_description")} <strong>{"{company_name}"}</strong> {t("telegram_and")} <strong>{"{greeting_company}"}</strong>{t("telegram_company_branding_hint")}
              </div>

              <div className={styles.previewButtons}>
                {languages.map((language) => (
                  <button
                    key={language.code}
                    type="button"
                    className={activeTextLang === language.code ? styles.primaryAction : styles.secondaryAction}
                    onClick={() => setActiveTextLang(language.code)}
                  >
                    {language.code.toUpperCase()} {language.enabled ? "" : `(${t("crm_settings_off")})`}
                  </button>
                ))}
              </div>

              <div className={styles.compactMessagesList}>
                {TEXT_FIELDS.map((key) => (
                  <div className={styles.compactMessageItem} key={key}>
                    <div className={styles.compactMessageMeta}>
                      <strong>{t(`telegram_text_field_${key}`)}</strong>
                      <span>{key}</span>
                    </div>
                    <textarea
                      className={styles.compactTextarea}
                      value={form.texts?.[key]?.[activeTextLang] || ""}
                      onChange={(e) => setTextValue(key, activeTextLang, e.target.value)}
                      rows={key === "welcome" ? 4 : 2}
                    />
                    {key === "welcome" && (
                      <div className={styles.cardSub}>
                        {t("telegram_template")} {"{greeting_company}"} {t("telegram_greeting_company_hint")} {"{company_name}"} {t("telegram_company_name_only_hint")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {activePageTab === "automation" && (
            <section className={styles.card}>
              <div className={styles.cardTitle}>{t("telegram_conversation_flow")}</div>
              <div className={styles.cardSub}>
                {t("telegram_conversation_flow_description")}
              </div>

              <div className={styles.row2}>
                <div className={styles.field}>
                  <label>{t("telegram_bot_scenario")}</label>
                  <select className={styles.textInput} value={form.conversation_flow || "product_orders"} onChange={(e) => setValue("conversation_flow", e.target.value)}>
                    {CONVERSATION_FLOWS.map((x) => <option key={x.value} value={x.value}>{t(x.label)}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label>{t("telegram_tenant_timezone")}</label>
                  <input className={styles.textInput} value={form.timezone || "Europe/Chisinau"} onChange={(e) => setValue("timezone", e.target.value)} />
                </div>
              </div>

              <div className={styles.switchGrid}>
                <label className={styles.switchCard}><span>{t("telegram_request_phone")}</span><input type="checkbox" checked={!!form.request_phone} onChange={(e) => setChecked("request_phone", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_create_contact")}</span><input type="checkbox" checked={!!form.create_contact} onChange={(e) => setChecked("create_contact", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_create_lead")}</span><input type="checkbox" checked={!!form.create_lead} onChange={(e) => setChecked("create_lead", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_create_task")}</span><input type="checkbox" checked={!!form.create_task} onChange={(e) => setChecked("create_task", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_notify_manager")}</span><input type="checkbox" checked={!!form.notify_manager} onChange={(e) => setChecked("notify_manager", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_product_catalog")}</span><input type="checkbox" checked={!!form.enable_catalog} onChange={(e) => setChecked("enable_catalog", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_manual_order_description")}</span><input type="checkbox" checked={!!form.enable_manual_order} onChange={(e) => setChecked("enable_manual_order", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_manager_request")}</span><input type="checkbox" checked={!!form.enable_manager_request} onChange={(e) => setChecked("enable_manager_request", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("preset_stage_real_estate_reservation")}</span><input type="checkbox" checked={!!form.enable_reservation} onChange={(e) => setChecked("enable_reservation", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_attach_description")}</span><input type="checkbox" checked={!!form.attach_order_description_to_lead} onChange={(e) => setChecked("attach_order_description_to_lead", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_show_price")}</span><input type="checkbox" checked={!!form.show_product_price} onChange={(e) => setChecked("show_product_price", e.target.checked)} /></label>
                <label className={styles.switchCard}><span>{t("telegram_show_stock")}</span><input type="checkbox" checked={!!form.show_stock} onChange={(e) => setChecked("show_stock", e.target.checked)} /></label>
              </div>

              <label className={styles.scheduleSyncCard}>
                <span className={styles.scheduleSyncCopy}>
                  <strong>{t("telegram_use_crm_free_time")}</strong>
                  <small>
                    {t("telegram_free_time_description")}
                  </small>
                </span>
                <input
                  type="checkbox"
                  checked={!!form.use_crm_work_schedule}
                  onChange={(e) => setChecked("use_crm_work_schedule", e.target.checked)}
                />
              </label>

              <div className={styles.row2}>
                <div className={styles.field}>
                  <label>{t("telegram_lead_source")}</label>
                  <input className={styles.textInput} value={form.lead_source || ""} onChange={(e) => setValue("lead_source", e.target.value)} />
                </div>
                <div className={styles.field}>
                  <label>{t("telegram_default_lead_status")}</label>
                  <select className={styles.textInput} value={form.lead_status || "new"} onChange={(e) => setValue("lead_status", e.target.value)}>
                    {LEAD_STATUSES.map((x) => <option key={x.value} value={x.value}>{t(x.label)}</option>)}
                  </select>
                </div>
              </div>
            </section>
          )}



          {activePageTab === "internal" && (
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <div>
                  <div className={styles.cardTitle}>{t("telegram_internal_sales_channel")}</div>
                  <div className={styles.cardSub}>
                    {t("telegram_internal_channel_description")}
                  </div>
                </div>

                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={!!form.internal_notifications_enabled}
                    onChange={(e) => setChecked("internal_notifications_enabled", e.target.checked)}
                  />
                  <span>{form.internal_notifications_enabled ? "Enabled" : "Disabled"}</span>
                </label>
              </div>

              <div className={styles.formGrid}>
                <label className={styles.formLabel}>{t("telegram_internal_chat_id")}</label>
                <input
                  className={styles.textInput}
                  value={form.internal_chat_id || ""}
                  placeholder="Example: -1001234567890"
                  onChange={(e) => setValue("internal_chat_id", e.target.value)}
                />

                <label className={styles.formLabel}>{t("telegram_open_crm_base_url")}</label>
                <input
                  className={styles.textInput}
                  value={form.internal_open_crm_base_url || ""}
                  placeholder={t("telegram_open_crm_url_placeholder")}
                  onChange={(e) => setValue("internal_open_crm_base_url", e.target.value)}
                />
              </div>

              <div className={styles.switchGrid}>
                <label className={styles.switchCard}>
                  <span>{t("telegram_notify_new_leads")}</span>
                  <input type="checkbox" checked={!!form.notify_new_leads} onChange={(e) => setChecked("notify_new_leads", e.target.checked)} />
                </label>
                <label className={styles.switchCard}>
                  <span>{t("telegram_bot_leads")}</span>
                  <input type="checkbox" checked={!!form.notify_bot_leads} onChange={(e) => setChecked("notify_bot_leads", e.target.checked)} />
                </label>
                <label className={styles.switchCard}>
                  <span>{t("telegram_manual_leads")}</span>
                  <input type="checkbox" checked={!!form.notify_manual_leads} onChange={(e) => setChecked("notify_manual_leads", e.target.checked)} />
                </label>
                <label className={styles.switchCard}>
                  <span>{t("telegram_api_form_leads")}</span>
                  <input type="checkbox" checked={!!form.notify_api_leads} onChange={(e) => setChecked("notify_api_leads", e.target.checked)} />
                </label>
                <label className={styles.switchCard}>
                  <span>{t("telegram_import_leads")}</span>
                  <input type="checkbox" checked={!!form.notify_import_leads} onChange={(e) => setChecked("notify_import_leads", e.target.checked)} />
                </label>
                <label className={styles.switchCard}>
                  <span>{t("telegram_allow_take_lead")}</span>
                  <input type="checkbox" checked={!!form.allow_assign_from_telegram} onChange={(e) => setChecked("allow_assign_from_telegram", e.target.checked)} />
                </label>
              </div>

              <div className={styles.row2}>
                <button className={styles.secondaryAction} type="button" onClick={testInternalChannel} disabled={disabled}>
                  {t("telegram_test_channel")}
                </button>
                <button className={styles.primaryAction} type="button" onClick={testInternalLeadNotification} disabled={disabled}>
                  {t("telegram_send_test_lead")}
                </button>
              </div>

              <div className={styles.cardSub} style={{ marginTop: 14 }}>
                {t("telegram_employee_link_hint")}
              </div>
            </section>
          )}

          {activePageTab === "dialog" && (
            <section className={styles.card}>
              <div className={styles.dialogHeader}>
                <div>
                  <div className={styles.cardTitle}>{t("telegram_dialog_builder")}</div>
                  <div className={styles.cardSub}>
                    {t("telegram_dialog_builder_description")}
                  </div>
                </div>
                <button className={styles.primaryAction} type="button" onClick={addDialogStep} disabled={disabled}>{t("telegram_add_question")}</button>
              </div>

              <div className={styles.systemActionsPanel}>
                <div className={styles.systemActionsHead}>
                  <div>
                    <div className={styles.cardTitle}>{t("telegram_system_actions")}</div>
                    <div className={styles.cardSub}>{t("telegram_system_actions_description")}</div>
                  </div>
                  <span className={styles.defaultBadge}>{t("telegram_builtin")}</span>
                </div>

                <div className={styles.systemActionsGrid}>
                  {systemActions.map((action) => (
                    <label key={action.id} className={action.enabled ? styles.systemActionCardOn : styles.systemActionCard}>
                      <span className={styles.systemActionIcon}>{action.id === "age_restriction" ? "🔞" : action.id === "manual_order" ? "📝" : action.id === "marketing" ? "🎁" : "🏷"}</span>
                      <span className={styles.systemActionText}>
                        <strong>{action.title}</strong>
                        <small>{t(action.subtitle)}</small>
                      </span>
                      <span className={action.enabled ? styles.statusOn : styles.statusOff}>{action.enabled ? t("crm_settings_on") : t("crm_settings_off")}</span>
                      <input
                        type="checkbox"
                        checked={action.enabled}
                        onChange={(e) => setChecked(action.field, e.target.checked)}
                        disabled={disabled}
                      />
                    </label>
                  ))}
                </div>

                {form.age_restriction_enabled && (
                  <div
                    className={styles.card}
                    style={{
                      marginTop: 12,
                      padding: "12px 14px",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    <div className={styles.field} style={{ width: 250, maxWidth: "100%" }}>
                      <label>{t("telegram_age_restriction_year_label")}</label>
                      <input
                        className={styles.textInput}
                        type="number"
                        min="1900"
                        max={new Date().getFullYear()}
                        step="1"
                        value={form.age_restriction_max_birth_year ?? ""}
                        placeholder="2012"
                        onChange={(e) => setValue("age_restriction_max_birth_year", e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                    <div className={styles.cardSub} style={{ paddingBottom: 10 }}>
                      {t("telegram_age_restriction_year_example")}
                    </div>
                  </div>
                )}

                <div className={styles.telegramKeyboardPreview}>
                  <span>{t("telegram_menu_preview")}</span>
                  <div>
                    {form.enable_manual_order !== false && <button type="button">{form.texts?.manual_order?.[activeTextLang] || DEFAULT_TEXTS.manual_order[activeTextLang] || DEFAULT_TEXTS.manual_order.en}</button>}
                    <div>
                      {form.enable_marketing_menu !== false && <button type="button">{form.texts?.marketing_button?.[activeTextLang] || DEFAULT_TEXTS.marketing_button[activeTextLang] || DEFAULT_TEXTS.marketing_button.en}</button>}
                      {form.enable_coupon_menu !== false && <button type="button">{form.texts?.coupon_button?.[activeTextLang] || DEFAULT_TEXTS.coupon_button[activeTextLang] || DEFAULT_TEXTS.coupon_button.en}</button>}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.dialogLayout}>
                <div className={styles.dialogList}>
                  {dialogSteps.map((step, index) => {
                    const active = expandedDialogStepId === step.id;
                    const lang = activeTextLang || form.default_language || "ru";
                    return (
                      <button key={step.id} type="button" className={active ? styles.dialogStepActive : styles.dialogStep} onClick={() => setExpandedDialogStepId(step.id)}>
                        <span className={styles.dragHandle}>☰</span>
                        <span className={styles.dialogStepIndex}>{index + 1}</span>
                        <span className={styles.dialogStepText}>
                          <strong>{step.question?.[lang] || step.question?.ru || "Untitled question"}</strong>
                          <small>{step.answer_type} · {step.required ? "required" : "optional"} · {step.save_to}</small>
                        </span>
                        <span className={step.enabled ? styles.statusOn : styles.statusOff}>{step.enabled ? "On" : "Off"}</span>
                      </button>
                    );
                  })}
                </div>

                <div className={styles.dialogEditor}>
                  {!expandedDialogStepId && <div className={styles.emptyState}>{t("telegram_select_question_hint")}</div>}
                  {dialogSteps.filter((step) => step.id === expandedDialogStepId).map((step) => {
                    const index = dialogSteps.findIndex((x) => x.id === step.id);
                    return (
                      <div key={step.id} className={styles.dialogEditorInner}>
                        <div className={styles.dialogEditorTop}>
                          <div>
                            <div className={styles.cardTitle}>{t("telegram_question_settings")}</div>
                            <div className={styles.cardSub}>{t("telegram_question_settings_description")}</div>
                          </div>
                          <div className={styles.dialogTopActions}>
                            <button type="button" className={styles.secondaryAction} onClick={() => moveDialogStep(step.id, -1)} disabled={disabled || index === 0}>↑</button>
                            <button type="button" className={styles.secondaryAction} onClick={() => moveDialogStep(step.id, 1)} disabled={disabled || index === dialogSteps.length - 1}>↓</button>
                            <button type="button" className={styles.dangerAction} onClick={() => removeDialogStep(step.id)} disabled={disabled || dialogSteps.length <= 1}>{t("delete")}</button>
                          </div>
                        </div>

                        <div className={styles.dialogSettingsGrid}>
                          <label className={styles.switchCard}><span>{t("telegram_enabled")}</span><input type="checkbox" checked={!!step.enabled} onChange={(e) => updateDialogStep(step.id, { enabled: e.target.checked })} /></label>
                          <label className={styles.switchCard}><span>{t("required")}</span><input type="checkbox" checked={!!step.required} onChange={(e) => updateDialogStep(step.id, { required: e.target.checked })} /></label>
                          <div className={styles.field}><label>{t("telegram_question_key")}</label><input className={styles.textInput} value={step.id} onChange={(e) => updateDialogStep(step.id, { id: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "_"), key: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "_") })} /></div>
                          <div className={styles.field}><label>{t("telegram_answer_type")}</label><select className={styles.selectInput} value={step.answer_type} onChange={(e) => updateDialogStep(step.id, { answer_type: e.target.value })}>{ANSWER_TYPES.map((item) => <option key={item.value} value={item.value}>{t(item.label)}</option>)}</select></div>
                          <div className={styles.field}><label>{t("telegram_validation")}</label><select className={styles.selectInput} value={step.validation} onChange={(e) => updateDialogStep(step.id, { validation: e.target.value })}>{VALIDATION_RULES.map((item) => <option key={item.value} value={item.value}>{t(item.label)}</option>)}</select></div>
                          <div className={styles.field}><label>{t("telegram_save_to_crm")}</label><select className={styles.selectInput} value={step.save_to} onChange={(e) => updateDialogStep(step.id, { save_to: e.target.value })}>{SAVE_TARGETS.map((item) => <option key={item.value} value={item.value}>{t(item.label)}</option>)}</select></div>
                        </div>

                        {(step.answer_type === "date" || step.answer_type === "datetime") && (
                          <div className={styles.availabilityCard}>
                            <div className={styles.availabilityHead}>
                              <div>
                                <strong>{t("telegram_available_days")}</strong>
                                <small>{t("telegram_available_days_description")}</small>
                              </div>
                            </div>
                            <div className={styles.dayModeGrid}>
                              {[
                                ["all", "All days", [0,1,2,3,4,5,6]],
                                ["weekdays", "Weekdays", [0,1,2,3,4]],
                                ["weekends", "Weekends", [5,6]],
                                ["custom", "Custom", step.date_allowed_weekdays || []],
                              ].map(([value, label, days]) => (
                                <button key={value} type="button" className={step.date_days_mode === value ? styles.dayModeActive : styles.dayMode} onClick={() => updateDialogStep(step.id, { date_days_mode: value, date_allowed_weekdays: days })}>
                                  {label}
                                </button>
                              ))}
                            </div>
                            {step.date_days_mode === "custom" && (
                              <div className={styles.weekdayPicker}>
                                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, day) => {
                                  const selected = (step.date_allowed_weekdays || []).includes(day);
                                  return (
                                    <button key={label} type="button" className={selected ? styles.weekdayActive : styles.weekday} onClick={() => {
                                      const current = step.date_allowed_weekdays || [];
                                      const next = selected ? current.filter((item) => item !== day) : [...current, day].sort();
                                      updateDialogStep(step.id, { date_allowed_weekdays: next });
                                    }}>{label}</button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {(step.answer_type === "time" || step.answer_type === "datetime") && (
                          <div className={styles.availabilityCard}>
                            <div className={styles.availabilityHead}><div><strong>Time source</strong><small>Use the shared CRM schedule or an independent range for this question.</small></div></div>
                            <div className={styles.field}><select className={styles.selectInput} value={step.time_source || "crm"} onChange={(e) => updateDialogStep(step.id, { time_source: e.target.value })}><option value="crm">CRM Work Schedule</option><option value="custom">Custom</option></select></div>
                            {(step.time_source || "crm") === "custom" && (<div className={styles.timeRangeGrid}><div className={styles.field}><label>{t("from")}</label><input type="time" className={styles.textInput} value={step.time_from || "09:00"} onChange={(e) => updateDialogStep(step.id, { time_from: e.target.value })} /></div><div className={styles.field}><label>{t("to")}</label><input type="time" className={styles.textInput} value={step.time_to || "18:00"} onChange={(e) => updateDialogStep(step.id, { time_to: e.target.value })} /></div><div className={styles.field}><label>Slot step (min)</label><input type="number" min="1" className={styles.textInput} value={step.time_step_minutes || 15} onChange={(e) => updateDialogStep(step.id, { time_step_minutes: Number(e.target.value) })} /></div></div>)}
                          </div>
                        )}

                        <div className={styles.tabsWrap}>
                          {languages.map((language) => (
                            <button key={language.code} type="button" className={activeTextLang === language.code ? styles.langTabActive : styles.langTab} onClick={() => setActiveTextLang(language.code)}>
                              {language.code.toUpperCase()} {language.enabled ? "" : "(off)"}
                            </button>
                          ))}
                        </div>

                        <div className={styles.dialogLangGrid}>
                          <div className={styles.field}>
                            <label>{t("telegram_question_text")}</label>
                            <textarea className={styles.messageTextarea} value={step.question?.[activeTextLang] || ""} onChange={(e) => updateDialogStepLang(step.id, "question", activeTextLang, e.target.value)} rows={3} placeholder={t("telegram_question_placeholder")} />
                          </div>
                          <div className={styles.field}>
                            <label>{t("telegram_answer_placeholder_label")}</label>
                            <textarea className={styles.messageTextarea} value={step.placeholder?.[activeTextLang] || ""} onChange={(e) => updateDialogStepLang(step.id, "placeholder", activeTextLang, e.target.value)} rows={2} placeholder={t("telegram_answer_placeholder")} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </div>

        <aside className={styles.sideCol}>
          <section className={styles.statusCard}>
            <div className={styles.statusHead}>
              <div>
                <div className={styles.cardTitle}>{t("telegram_bot_status")}</div>
                <div className={styles.cardSub}>{t("telegram_readiness_overview")}</div>
              </div>
              <span className={form.enabled ? styles.statusOn : styles.statusOff}>{form.enabled ? "On" : "Off"}</span>
            </div>

            <div className={styles.statusRows}>
              <div><span>{t("telegram_token")}</span><strong>{hasToken ? "Configured" : "Not configured"}</strong></div>
              <div><span>{t("username")}</span><strong>{form.bot_username ? `@${form.bot_username}` : t("not_set")}</strong></div>
              <div><span>{t("telegram_scenario")}</span><strong>{form.conversation_flow || "product_orders"}</strong></div>
              <div><span>{t("telegram_languages")}</span><strong>{enabledLanguages.map((x) => x.code.toUpperCase()).join(", ") || t("none")}</strong></div>
              <div><span>{t("contact_custom_field_column_default")}</span><strong>{(form.default_language || "ru").toUpperCase()}</strong></div>
              <div><span>{t("telegram_webhook")}</span><strong>{form.webhook_is_set ? t("active") : t("not_active")}</strong></div>
            </div>

            <button className={styles.fullPrimary} onClick={setWebhook} disabled={disabled}>{t("telegram_set_webhook")}</button>
            <button className={styles.fullSecondary} onClick={testBot} disabled={disabled}>{t("telegram_test_bot")}</button>
            <button className={styles.fullDanger} onClick={deleteWebhook} disabled={disabled}>{t("telegram_disable_webhook")}</button>
          </section>

          <section className={styles.statusCard}>
            <div className={styles.statusHead}>
              <div>
                <div className={styles.cardTitle}>
                  {t("telegram_maintenance")}
                  <span
                    title={t("telegram_maintenance_actions_hint")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      marginLeft: 8,
                      borderRadius: "50%",
                      border: "1px solid rgba(255,255,255,0.25)",
                      fontSize: 12,
                      cursor: "help",
                      opacity: 0.85,
                    }}
                  >
                    i
                  </span>
                </div>

                <div className={styles.cardSub}>
                  {t("telegram_maintenance_description")}
                </div>
              </div>
            </div>

            <div className={styles.statusRows}>
              <div><span>{t("telegram_total_sessions")}</span><strong>{sessionStats.total_sessions}</strong></div>
              <div><span>{t("telegram_waiting_replies")}</span><strong>{sessionStats.waiting_sessions}</strong></div>
              <div><span>{t("telegram_active_dialogs")}</span><strong>{sessionStats.order_dialog_sessions}</strong></div>
            </div>

            <button className={styles.fullSecondary} onClick={loadSessionStats} disabled={disabled}>
              {t("telegram_refresh_stats")}
            </button>
            <button className={styles.fullSecondary} onClick={restartFlows} disabled={disabled}>
              {t("telegram_restart_active_flows")}
            </button>
            <button className={styles.fullDanger} onClick={resetAllSessions} disabled={disabled}>
              {t("telegram_reset_all_sessions")}
            </button>
            <button className={styles.fullPrimary} onClick={resetAndSendWelcome} disabled={disabled}>
              {t("telegram_reset_send_welcome")}
            </button>
          </section>

          <section className={styles.previewCard}>
            <div className={styles.previewTitle}>{t("telegram_preview")}</div>
            <div className={styles.flowStep}><span>1</span><strong>{t("language")}</strong><p>{form.enable_language_selection ? enabledLanguages.map((x) => x.label).join(" / ") : t("telegram_default_language", { language: form.default_language })}</p></div>
            <div className={styles.flowStep}><span>2</span><strong>{t("telegram_phone_auth")}</strong><p>{t("telegram_phone_auth_description")}</p></div>
            <div className={styles.flowStep}><span>3</span><strong>{t("telegram_crm_automation")}</strong><p>{[form.create_contact ? t("contacts") : null, form.create_lead ? t("leads") : null, form.create_task ? t("tasks") : null, form.notify_manager ? t("telegram_manager_notify") : null].filter(Boolean).join(" / ")}</p></div>
            <div className={styles.chatBubble}>
              {renderTelegramTemplatePreview(
                form.texts?.welcome?.[form.default_language || "ru"] || "Welcome text is empty.",
                "Company Name",
                form.default_language || "en"
              )}
            </div>
            <div className={styles.dialogPreviewMini}>
              {dialogSteps.filter((step) => step.enabled).slice(0, 6).map((step, idx) => (
                <div key={step.id} className={styles.dialogPreviewRow}>
                  <span>{idx + 1}</span>
                  <strong>{step.question?.[form.default_language || "ru"] || step.question?.ru || "Question"}</strong>
                  <small>{step.answer_type} · {step.required ? "required" : "optional"}</small>
                </div>
              ))}
            </div>
            <div className={styles.previewButtons}>
              {form.enable_manual_order && <span>{form.texts?.manual_order?.[form.default_language || "ru"] || "📝 Describe order"}</span>}
              {form.request_phone && <span>{form.texts?.share_phone?.[form.default_language || "ru"] || "📞 Share phone"}</span>}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
